<template>
  <b-form
    v-if="initialised"
    @submit.prevent="onSubmit"
    @keydown="form.errors.clear($event.target.name)"
  >
    <div class="row mb-3">
      <div class="col d-flex align-items-center">
        <img
          v-if="$lodash.get(crud, 'data.job.brand.logo_horizontal')"
          :src="$lodash.get(crud, 'data.job.brand.logo_horizontal')"
          :alt="$lodash.get(crud, 'data.job.brand.name')"
          height="85"
        />
        <b v-else class="text-blue text-28">{{ $lodash.get(crud, "data.job.brand.name") }}</b>
      </div>
      <div class="col d-flex flex-wrap text-center align-items-center main-heading">
        <h1 v-if="invoiceNumber" class="w-100 text-blue text-28">
          {{ form.is_quote ? "Quote" : "Invoice" }}
          {{ invoiceNumber | trimLeadingZeros }}
        </h1>
        <div v-if="crud.data.created_at" class="w-100">
          Created: {{ formatLocalDate(crud.data.created_at) }}
        </div>
      </div>
      <div class="col d-flex justify-content-end align-items-center">
        <a
          href=""
          target="_blank"
          class="d-flex flex-wrap justify-content-center align-items-center relative"
          @click.prevent="openPdf"
        >
          <div v-if="generatingPdf" class="text-center">
            <font-awesome-icon spin :icon="['fal', 'spinner']" class="text-32" />
            <div class="flex-break"></div>
            <span class="text-13 d-block pt-1">Generating</span>
          </div>
          <div v-else class="text-center">
            <font-awesome-icon :icon="['fal', 'file-pdf']" class="text-32" />
            <div class="flex-break"></div>
            <span class="text-13 d-block pt-1">Download PDF</span>
          </div>
        </a>
      </div>
    </div>
    <div class="row">
      <div class="col-6">
        <b-card>
          <template #header>
            <strong>General</strong>
          </template>

          <template v-if="creating">
            <div class="form-group row" :class="{ 'has-danger': form.errors.has('is_quote') }">
              <div class="col-3 col-form-label text-right">Quote</div>
              <div class="col-8">
                <core-switch
                  v-model="form.is_quote"
                  variant="3d"
                  color="primary"
                  on="Q"
                  off="I"
                ></core-switch>
                <small
                  v-if="form.errors.has('is_quote')"
                  class="invalid-feedback"
                  v-text="form.errors.get('is_quote')"
                ></small>
              </div>
            </div>
            <div v-if="$can('read', 'user')" class="row">
              <div class="col">
                <form-user-select
                  ref="createdBy"
                  v-model="form.created_by_user"
                  name="created_by"
                  :filter-by-brand-id="crud.data.job.brand_id"
                  placeholder="Search technician..."
                  @change="updateCreatedBy"
                >
                  Technician
                </form-user-select>
              </div>
            </div>
          </template>

          <form-select
            v-if="!form.is_quote"
            v-model="form.status"
            name="status"
            :options="statuses"
            :disabled="true"
          />
          <form-select
            v-model="form.warranty_option_id"
            name="warranty_option_id"
            :options="warrantyDurations"
            :disabled="readonly"
          >
            Warranty
          </form-select>

          <div
            class="form-group row"
            :class="{
              'has-danger': form.errors.has('royalty_type') || form.errors.has('royalty_value'),
            }"
          >
            <div class="col-3 col-form-label text-right">Tech Royalty</div>
            <div class="col-8">
              <div class="row">
                <div class="col">
                  <form-select
                    v-model="form.royalty_type"
                    name="royalty_type"
                    :options="royaltyTypes"
                    :disabled="readonly"
                    :labelless="true"
                    bottom-margin="0"
                    @input="royaltyTypeChanged"
                  />
                </div>
                <div v-if="form.royalty_type !== 'Standard'" class="col">
                  <form-text
                    v-model="form.royalty_value"
                    name="royalty_value"
                    :disabled="readonly"
                    :labelless="true"
                    bottom-margin="0"
                    :is-number="true"
                    min="0"
                    :max="form.royalty_type === 'Percentage' ? 100 : null"
                  >
                    <template v-if="form.royalty_type === 'Percentage'" #append>
                      <span class="input-group-text">%</span>
                    </template>
                    <template v-if="form.royalty_type === 'Ceiling'" #prepend>
                      <span class="input-group-text">$</span>
                    </template>
                  </form-text>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group row mb-0">
            <div class="col-3 col-form-label text-right">Disable Credit Card</div>
            <div class="col-8">
              <core-switch
                v-model="form.disable_credit_card"
                variant="3d"
                color="primary"
                :disabled="hasPoReference"
                @change="form.errors.clear('disable_credit_card')"
              />
            </div>
          </div>

          <template #footer>
            <div v-if="hasPoReference" class="row">
              <label class="col-3 text-right"> Purchase Order Reference </label>
              <div class="col-8">
                {{ crud.data.job.po_reference }}
              </div>
            </div>
            <div class="row">
              <label class="col-3 text-right">Technician</label>
              <div class="col-8">
                <user-object
                  v-if="crud.data.creator"
                  :name="crud.data.creator.name"
                  :entity-id="crud.data.creator.id"
                  :open-new="true"
                />
                <div v-else>Not Assigned</div>
              </div>
            </div>
            <template v-if="crud.data.status === 'Paid' || crud.data.status === 'Refunded'">
              <div class="row">
                <label class="col-3 text-right"> Payment Method </label>
                <div class="col-8">
                  {{ crud.data.payment_method }}
                </div>
              </div>
              <div class="row">
                <label class="col-3 text-right"> Paid At </label>
                <div class="col-8">
                  {{ formatLocalDate(crud.data.paid_at) }}
                </div>
              </div>
            </template>
          </template>
        </b-card>
      </div>
      <div class="col-6">
        <b-card>
          <template #header>
            <div class="d-flex">
              <div class="mr-auto">
                <strong>Job & Billing</strong>
                Information
              </div>
              <div v-if="crud.data.job">
                <job-object :job-number="crud.data.job.job_number" :entity-id="crud.data.job.id" />
              </div>
            </div>
          </template>

          <div class="row">
            <div class="col">
              <h2 class="text-15 text-blue">Billing Details</h2>
              <div class="mb-1">
                <customer-object
                  v-if="$lodash.has(crud, 'data.job.billing_contacts[0].id')"
                  :name="crud.data.job.billing_contacts[0].name"
                  :entity-id="crud.data.job.billing_contacts[0].customer_id"
                  :open-new="true"
                />
                <customer-object
                  v-else-if="$lodash.has(crud, 'data.job.job_contacts[0].id')"
                  :name="crud.data.job.job_contacts[0].name"
                  :entity-id="crud.data.job.job_contacts[0].customer_id"
                  :open-new="true"
                />
              </div>
              <div>{{ $lodash.get(crud.data, "job.billing_address.shortAddress") }}</div>
              <div>
                {{ $lodash.get(crud.data, "job.billing_address.state") }}
                {{ $lodash.get(crud.data, "job.billing_address.postcode") }}
              </div>
            </div>
            <div class="col">
              <h2 class="text-15 text-blue">Job Contact</h2>
              <div class="mb-1">
                <customer-object
                  v-if="$lodash.has(crud, 'data.job.job_contacts[0].id')"
                  :name="crud.data.job.job_contacts[0].name"
                  :entity-id="crud.data.job.job_contacts[0].customer_id"
                  :open-new="true"
                />
              </div>
              <div>{{ $lodash.get(crud.data, "job.job_address.shortAddress") }}</div>
              <div>
                {{ $lodash.get(crud.data, "job.job_address.state") }}
                {{ $lodash.get(crud.data, "job.job_address.postcode") }}
              </div>
            </div>
          </div>
        </b-card>
      </div>
    </div>

    <b-card>
      <template #header>
        <strong>Invoice</strong>
        Items
      </template>

      <table class="table table-striped table-hover">
        <thead>
          <tr>
            <th>Title</th>
            <th>Description</th>
            <th>Unit Cost</th>
            <th>Quantity</th>
            <th>Total</th>
            <th>Hide Qty?</th>
            <th v-if="!readonly"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in crud.data.items" v-show="!item.voucher_id" :key="index">
            <td>
              <multiselect
                :id="index"
                v-model.trim="item.title"
                :options="validBrandInvoiceItems"
                :close-on-select="true"
                :show-labels="false"
                :taggable="true"
                tag-placeholder="Hit enter to add this title"
                :disabled="readonly"
                @tag="addCustomTitle"
                @select="invoiceItemSelect"
              />
              <div
                class="text-12 text-right"
                :class="item?.title?.length > 52 ? 'text-red' : 'text-muted'"
              >
                {{ item?.title?.length || 0 }} / 52
              </div>
              <div v-if="item?.service_type_id" class="mt-1 pl-1 text-13">
                <b>Related Service: </b><br />
                <span v-if="item?.service_type_name">{{ item?.service_type_name }}</span>
                <span v-else class="text-red">Related service deleted</span>
              </div>
            </td>
            <td>
              <textarea
                v-model.trim="item.description"
                class="form-control"
                :class="{ 'is-invalid': form.errors.has(`${index}.description`) }"
                style="height: 56px"
                :disabled="readonly"
                @keydown="form.errors.clear()"
              ></textarea>
              <div
                class="text-12 text-right"
                :class="item?.description?.length > 122 ? 'text-red' : 'text-muted'"
              >
                {{ item?.description?.length || 0 }} / 122
              </div>
            </td>
            <td>
              <input
                v-if="!item.voucher_id"
                v-model.trim="item.cost"
                type="number"
                step="0.01"
                class="form-control text-right"
                :class="{ 'is-invalid': form.errors.has(`${index}.cost`) }"
                :disabled="readonly"
                @keydown="form.errors.clear()"
              />
            </td>
            <td>
              <input
                v-show="!item.hide_qty"
                v-model.trim="item.quantity"
                type="number"
                min="0"
                step="0.01"
                class="form-control text-center"
                :class="{ 'is-invalid': form.errors.has(`${index}.quantity`) }"
                :disabled="readonly"
                @keydown="form.errors.clear()"
              />
            </td>
            <td>
              <input
                type="text"
                min="0"
                class="form-control text-right"
                readonly="readonly"
                :value="formatCurrency(item.cost * item.quantity, '')"
              />
            </td>
            <td>
              <core-switch
                v-model="item.hide_qty"
                variant="3d"
                color="primary"
                on="Y"
                off="N"
                :disabled="readonly"
                :checked="item.hide_qty === 1"
              />
            </td>
            <td v-if="!readonly">
              <font-awesome-icon
                v-if="!item.voucher_id"
                :icon="['fal', 'times']"
                class="cursor"
                @click.stop="deleteItem(index)"
              />
            </td>
          </tr>
          <tr v-if="voucherLineItem">
            <td>
              <input
                v-model.trim="voucherLineItem.title"
                type="text"
                class="form-control"
                disabled="disabled"
              />
            </td>
            <td>
              <textarea
                v-model.trim="voucherLineItem.description"
                class="form-control"
                style="height: 35px"
                readonly="readonly"
              />
            </td>
            <td>
              <input
                type="text"
                class="form-control text-right"
                readonly="readonly"
                :value="formatCurrency(voucherLineItem.cost, '')"
              />
            </td>
            <td>
              <input
                v-model.trim="voucherLineItem.quantity"
                type="number"
                min="0"
                class="form-control text-center"
                readonly="readonly"
              />
            </td>
            <td>
              <input
                type="text"
                class="form-control text-right"
                readonly="readonly"
                :value="formatCurrency(voucherLineItem.cost * voucherLineItem.quantity, '')"
              />
            </td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
        <tfoot v-if="!readonly">
          <tr>
            <td colspan="4">
              <button type="button" class="btn btn-primary btn-sm" @click.stop="addItem()">
                <font-awesome-icon :icon="['fal', 'plus-circle']" />
                Add Invoice Item
              </button>
            </td>
            <td colspan="3">
              <div class="voucher-code-wrapper">
                <form-select
                  v-model="form.voucher_code"
                  name="voucher_code"
                  :options="voucherOptions"
                  @input="voucherChanged"
                >
                  Voucher
                </form-select>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>

      <div class="row">
        <div class="col-4">
          <form-textarea v-model="form.note" name="note" :disabled="readonly" :single-row="false" />

          <div
            class="text-12 text-right -mt-3"
            :class="form.note?.length > 280 ? 'text-red' : 'text-muted'"
          >
            {{ form.note?.length || 0 }} / 280
          </div>
        </div>
        <div class="col-8 d-flex justify-content-end align-items-start">
          <div class="total-div">
            <div class="row">
              <div class="col-5 text-center">
                <b class="text-16">SUBTOTAL</b><br />
                <div class="text-muted text-12" style="margin-top: -4px">
                  <i>(Including GST)</i>
                </div>
              </div>
              <div class="col-7 d-flex justify-content-end align-items-center">
                <b class="text-24">{{ total }}</b>
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-5 text-center">
                <b class="text-16">GST</b><br />
                <div class="text-muted text-12" style="margin-top: -4px">
                  <i>(Included in Total)</i>
                </div>
              </div>
              <div class="col-7 d-flex justify-content-end align-items-center">
                <b class="text-16 text-muted">{{ totalGst }}</b>
              </div>
            </div>
            <template
              v-if="
                crud.data.status === 'Paid' &&
                (crud.data.transaction_note.startsWith('ch_') ||
                  crud.data.transaction_note.startsWith('paychoice_'))
              "
            >
              <div class="row mt-2">
                <div class="col-5 text-center"><b class="text-16">CARD FEE</b><br /></div>
                <div class="col-7 d-flex justify-content-end align-items-center">
                  <b class="text-16 text-muted">{{
                    formatCurrency(
                      crud.data.total_with_card_fee -
                        parseFloat(total.replace("$", "").replace(",", ""))
                    )
                  }}</b>
                </div>
              </div>
            </template>
            <div class="row mt-2">
              <div class="col-5 text-center">
                <b class="text-16">TOTAL</b><br />
                <div class="text-muted text-12" style="margin-top: -4px">
                  <i>(Including GST)</i>
                </div>
              </div>
              <div class="col-7 d-flex justify-content-end align-items-center">
                <template v-if="crud.data.status === 'Paid'">
                  <b class="text-16 text-muted">
                    {{ formatCurrency(crud.data.total) }}
                  </b>
                </template>
                <template v-else>
                  <b class="text-16 text-muted">{{ total }}</b>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <b-card v-if="form.errors.any()">
      <div class="col-6 push-3 has-danger">
        <div class="form-control-feedback">
          Invalid input detected. Please correct the input.

          <div v-for="(error, index) in form.errors.errors" :key="index">
            {{ error[0] }}
          </div>
        </div>
      </div>
    </b-card>

    <b-card-footer class="mb-5 d-flex">
      <span
        v-tooltip="{
          content: readonly ? 'A paid/refunded invoice cannot be updated' : '',
          classes: ['badge-theme'],
          offset: 7,
        }"
        class="mr-auto"
      >
        <button class="btn btn-primary" :disabled="form.notReady() || readonly">
          <font-awesome-icon v-if="form.busy" spin :icon="['fal', 'spinner']" />
          <font-awesome-icon v-else :icon="['fal', 'check']" />
          {{ crud.getAction() === "create" ? "Create" : "Save" }}
        </button>
      </span>

      <template v-if="!creating">
        <button class="btn btn-warning ml-2" :disabled="sending" @click.prevent="send()">
          <font-awesome-icon v-if="sending" spin :icon="['fal', 'spinner']" />
          <font-awesome-icon v-else :icon="['fal', 'envelope']" />
          <template v-if="crud.data.sent_at"> Resend</template>
          <template v-else> Send</template>
        </button>

        <template v-if="crud.data.is_quote">
          <template v-if="crud.data.accepted">
            <button class="btn btn-primary disabled ml-2" @click.prevent="process()">
              <font-awesome-icon :icon="['fal', 'thumbs-up']" />
              Accepted
            </button>
            <button
              class="btn btn-danger ml-2"
              :disabled="form.notReady() || crud.data.created_by === null"
              @click.prevent="createInvoiceByQuote()"
            >
              <font-awesome-icon v-if="form.busy" spin :icon="['fal', 'spinner']" />
              <font-awesome-icon v-else :icon="['fal', 'clone']" />
              Create Invoice
            </button>
          </template>
          <template v-else>
            <button
              class="btn btn-primary ml-2"
              :disabled="form.notReady()"
              @click.prevent="process()"
            >
              <font-awesome-icon :icon="['fal', 'thumbs-up']" />
              Accept
            </button>
          </template>
        </template>

        <template v-else>
          <template v-if="crud.data.status === 'Paid'">
            <template
              v-if="
                crud.data.transaction_note &&
                crud.data.transaction_note !== 'Bank Transfer' &&
                crud.data.transaction_note !== 'Cash Payment'
              "
            >
              <button
                class="btn btn-danger ml-2"
                :disabled="form.notReady() || refunding"
                @click.prevent="refund()"
              >
                <font-awesome-icon v-if="refunding" spin :icon="['fal', 'spinner']" />
                <font-awesome-icon v-else :icon="['fal', 'credit-card']" />
                Refund
              </button>
            </template>
            <template v-else>
              <button class="btn btn-primary disabled ml-2" @click.prevent="">
                <font-awesome-icon :icon="['fal', 'credit-card']" />
                Paid
              </button>
            </template>

            <button
              v-if="$can('revert', 'invoice')"
              class="btn btn-outline-danger ml-2"
              :disabled="form.notReady()"
              @click.prevent="unpay()"
            >
              <font-awesome-icon :icon="['fal', 'exclamation-triangle']" />
              UNPAY
            </button>
          </template>
          <template v-else-if="crud.data.status === 'Refunded'">
            <button class="btn btn-danger disabled ml-2" @click.prevent="">
              <font-awesome-icon :icon="['fal', 'credit-card']" />
              Refunded
            </button>
          </template>
          <template v-else>
            <button
              class="btn btn-primary ml-2"
              :disabled="form.notReady() || readonly"
              @click.prevent="payModal()"
            >
              <font-awesome-icon v-if="form.busy" spin :icon="['fal', 'spinner']" />
              <font-awesome-icon v-else :icon="['fal', 'envelope-open-dollar']" />
              Pay
            </button>
          </template>
        </template>
      </template>
    </b-card-footer>
  </b-form>
</template>

<script type="text/babel">
import format from "../../mixins/format";

export default {
  mixins: [format],

  data() {
    return {
      initialised: false,
      previewUrl: null,
      sending: false,
      creating: false,
      generatingPdf: false,
      jobId: null,
      validBrandInvoiceItems: [],
      allVouchers: [],
      selectedNewVoucher: null,

      statuses: ["Pending", "Sent", "Paid"],

      warrantyDurations: [],

      royaltyTypes: ["Standard", "Percentage", "Ceiling"],

      refunding: false,

      crud: null,
      form: new window.AppForm({
        created_by: null,
        created_by_user: {},
        is_quote: false,
        voucher_code: null,
        items: [],
        job_id: "",
        note: "",
        payment_method: null,
        // require_created_by: true,
        status: "",
        warranty_option_id: null,
        royalty_type: "Standard",
        royalty_value: "",
        disable_credit_card: false,
      }),
    };
  },

  computed: {
    hasPoReference() {
      return this.crud.data.job && this.crud.data.job.po_reference;
    },
    total() {
      let total = 0;
      let totalWithoutVoucherLine = 0;

      // Calculate total after manually selected a new voucher code
      if (this.selectedNewVoucher) {
        if (this.crud && this.crud.data && this.crud.data.items) {
          this.crud.data.items.forEach((item) => {
            if (!item.voucher_id) {
              totalWithoutVoucherLine += item.quantity * item.cost;
            }
          });
        }

        switch (this.selectedNewVoucher.type) {
          case "Amount":
            total = totalWithoutVoucherLine - this.selectedNewVoucher.value;
            break;

          case "Percentage":
            total = ((100 - this.selectedNewVoucher.value) / 100) * this.totalWithoutVoucherLine;
            break;

          default:
            total = totalWithoutVoucherLine;
        }

        if (total < 0) {
          total = 0;
        }

        return this.formatCurrency(total);
      }

      // Default total calulcated from crud.data.items
      if (this.crud && this.crud.data && this.crud.data.items) {
        this.crud.data.items.forEach((item) => {
          total += item.quantity * item.cost;
        });
      }

      if (total < 0) {
        total = 0;
      }

      return this.formatCurrency(total);
    },
    totalWithoutVoucherLine() {
      let totalWithoutVoucherLine = 0;

      if (this.crud && this.crud.data && this.crud.data.items) {
        this.crud.data.items.forEach((item) => {
          if (!item.voucher_id) {
            totalWithoutVoucherLine += item.quantity * item.cost;
          }
        });
      }

      if (totalWithoutVoucherLine < 0) {
        totalWithoutVoucherLine = 0;
      }

      return totalWithoutVoucherLine;
    },
    totalGst() {
      return this.formatCurrencyGST(this.total, 10);
    },
    readonly() {
      return (
        (this.crud.data.status === "Paid" || this.crud.data.status === "Refunded") &&
        !this.crud.data.is_quote
      );
    },
    invoiceNumber() {
      if (this.creating) {
        return `${this.crud.data.job.job_number}${this.form.is_quote ? "Q" : "I"}`;
      }

      return this.crud.data.invoice_number;
    },
    voucherLineItem() {
      let item = null;

      if (this.selectedNewVoucher) {
        let cost = 0;
        switch (this.selectedNewVoucher.type) {
          case "Amount":
            cost = -1 * this.selectedNewVoucher.value;
            break;

          case "Percentage":
            cost = -1 * ((this.selectedNewVoucher.value / 100) * this.totalWithoutVoucherLine);
            break;
        }

        return {
          cost: cost,
          description: this.selectedNewVoucher.description,
          ethereal: false,
          hide_qty: true,
          id: null,
          invoice_id: this.crud.data.id,
          quantity: 1,
          title: "Voucher Discount",
          voucher: this.selectedNewVoucher,
          voucher_id: this.selectedNewVoucher.id,
        };
      }

      if (this.crud && this.crud.data && this.crud.data.items) {
        const filteredItems = this.crud.data.items.filter((item) => item.voucher_id);
        if (filteredItems && filteredItems.length === 1) {
          item = filteredItems[0];
        }
        return item;
      }

      return item;
    },
    voucherOptions() {
      const vouchers = [
        {
          id: null,
          name: "Select Voucher",
        },
      ];
      return vouchers.concat(
        this.allVouchers.map((voucher) => ({
          id: voucher.code,
          name:
            voucher.type === "Amount"
              ? `${voucher.code}: -$${voucher.value}`
              : `${voucher.code}: -${voucher.value}%`,
        }))
      );
    },
  },

  watch: {
    totalWithoutVoucherLine() {
      // Detect total change, if changed on a "Perentage" type vouchered invoice
      // Update the voucher line based on new total
      this.refreshActualVoucherLineItem();
    },
  },

  created() {
    this.init();
  },

  methods: {
    refreshActualVoucherLineItem() {
      let voucher;
      if (this.selectedNewVoucher) {
        voucher = this.selectedNewVoucher;
      } else {
        voucher = this.$lodash.get(this.voucherLineItem, "voucher");
      }

      if (voucher && voucher.type && voucher.type === "Percentage") {
        if (this.crud && this.crud.data && this.crud.data.items) {
          let voucherIndex = null;
          this.crud.data.items.forEach((item, index) => {
            if (item.voucher_id) {
              voucherIndex = index;
            }
          });

          if (!voucherIndex) {
            return;
          }

          this.crud.data.items[voucherIndex].cost =
            (voucher.value / 100) * this.totalWithoutVoucherLine * -1;
        }
      }
    },

    async init() {
      this.crud = new window.AppCrud("invoice");

      const response = await this.$http.get(`/vouchers?is_valid=true`);
      if (response.body && response.body.data) {
        this.allVouchers = response.body.data;
      }

      const { jobId } = this.$router.currentRoute.params;
      if (jobId) {
        this.creating = true;
        this.jobId = jobId;
        return this.initCreate();
      }

      await this.crud.initFormData(this.form);

      this.previewUrl = `${process.env.VUE_APP_API}/invoices/${this.crud.data.uuid}/preview-by-uuid`;
      this.form.payment_method = null;
      this.validBrandInvoiceItems = this.crud.data.job.brand.invoice_items.map((i) => i.title);

      if (this.crud.data.status === "Refunded") {
        this.statuses = ["Pending", "Sent", "Paid", "Refunded"];
      } else {
        this.statuses = ["Pending", "Sent", "Paid"];
      }

      // Browser Title
      if (this.$lodash.get(this.crud, "data.invoice_number")) {
        // noinspection JSUnresolvedFunction
        this.$root.setTitle(`Invoice - ${this.$lodash.get(this.crud, "data.invoice_number")}`);
      }

      if (this.hasPoReference) {
        this.form.disable_credit_card = true;
      }

      this.warrantyDurations = (this.crud.data.job.brand.warranty_options || []).filter(
        (warranty) => !warranty.is_legacy || this.form.warranty_option_id === warranty.id
      );
      this.warrantyDurations.forEach((warranty) => {
        if (warranty.is_legacy) {
          warranty.name = `${warranty.name} (Legacy)`;
        }
      });

      this.initialised = true;
    },

    initCreate() {
      this.$http
        .get(
          `/jobs/${this.jobId}/?with=brand.invoiceItems.serviceType;brand.warrantyOptions;jobContacts;billingContacts`
        )
        .then((response) => {
          // Initialise crud.data
          const data = {
            accepted: false,
            is_quote: true,
            items: [],
            job: response.body,
            job_id: this.jobId,
            note: "",
            payment_method: null,
            status: "Pending",
            total: "",
            warranty_option_id: null,
            royalty_type: "Standard",
            royalty_value: "",
          };

          this.warrantyDurations = response.body.brand.warranty_options || [];
          this.warrantyDurations = this.warrantyDurations.filter((warranty) => !warranty.is_legacy);

          this.crud.data = data;
          this.form.loadData(data);

          // Initialise created_by as self if no permission to read users
          if (!this.$can("read", "user")) {
            this.form.created_by = this.$auth.user().id;
          }

          if (this.hasPoReference) {
            this.form.disable_credit_card = true;
          }

          this.form.finishProcessing();

          this.validBrandInvoiceItems = this.crud.data.job.brand.invoice_items.map((i) => i.title);

          this.form.job_id = this.jobId;
          this.initialised = true;
        });
    },

    onSubmit() {
      const action = this.creating ? "created" : "updated";

      if (this.form.voucher_code) {
        this.form.voucher_code = this.form.voucher_code.toUpperCase();
      }

      if (this.hasPoReference) {
        this.form.disable_credit_card = true;
      }

      if (!this.form.is_quote && this.form.status === "Paid") {
        return window
          .sweetAlert({
            className: "center-buttons",
            title: "Payment method",
            text: "Please provide the payment method",
            icon: "info",
            dangerMode: true,
            buttons: {
              cancel: "Cancel",
              Cash: "Cash",
            },
          })
          .then((choice) => {
            if (choice) {
              this.form.payment_method = choice;
              this.saveInvoice(`Invoice created and marked as Paid by ${choice}!`);
            }
          });
      }

      if (!this.form.is_quote && this.total === "$0.00") {
        return window
          .sweetAlert({
            title: "Zero amount invoice",
            text:
              "This invoice will be marked as paid automatically, and no further change will be possible!",
            icon: "warning",
            dangerMode: true,
            buttons: {
              cancel: "No",
              confirm: "Yes!",
            },
          })
          .then((confirmed) => {
            if (confirmed) {
              this.saveInvoice(`Invoice ${action} and marked as Paid!`);
            }
          });
      }

      return this.saveInvoice(`Invoice ${action}`);
    },

    saveInvoice(message) {
      this.form.items = this.crud.data.items;
      if (!this.form.is_quote) {
        this.form.accepted = false;
      }

      this.crud.submit(this.form).then((response) => {
        window.toastr.success(message, "Success");

        if (this.creating) {
          this.$router.push(
            {
              name: "invoices.edit",
              params: { id: response.id },
            },
            () => {
              // noinspection JSUnresolvedFunction
              this.$root.reload();
            }
          );
        } else {
          this.init();
        }
      });
    },

    addItem() {
      this.crud.data.items.push({
        cost: "",
        description: "",
        quantity: 1,
        title: "",
        hide_qty: false,
      });

      this.form.errors.clear();
    },

    deleteItem(index) {
      this.crud.data.items.splice(index, 1);

      this.form.errors.clear();
    },

    updateCreatedBy(user) {
      this.form.created_by_user = user;
      this.form.created_by = user.id;

      this.form.errors.clear("created_by");
    },

    openPdf() {
      if (this.generatingPdf) {
        return;
      }

      this.generatingPdf = true;

      this.$http
        .get(`/invoices/${this.crud.data.id}/pdf`, { responseType: "arraybuffer" })
        .then((response) => {
          // noinspection JSUnresolvedFunction
          const blob = new Blob([response.data], { type: response.headers.get("content-type") });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = `${this.crud.data.is_quote ? "Quote" : "Invoice"} ${
            this.crud.data.invoice_number
          }.pdf`;
          link.click();

          this.generatingPdf = false;
        });
    },

    process() {
      if (this.crud.data.is_quote) {
        if (!this.crud.data.accepted) {
          window.open(
            `/invoices/${this.crud.data.uuid}/accept-quote?utm_source=direct&utm_medium=CCT&utm_campaign=DL-Q0`
          );
        }
      } else if (this.crud.data.status !== "Paid") {
        window.open(
          `/invoices/${this.crud.data.uuid}/pay?utm_source=direct&utm_medium=CCT&utm_campaign=DL-I0`
        );
      }
    },

    async payModal() {
      if (this.form.busy) {
        return;
      }

      const confirmed = await window.sweetAlert({
        title: "Please confirm",
        text: "Select how you want to pay for this invoice",
        icon: "warning",
        dangerMode: true,
        buttons: {
          card: "Credit Card",
          bank: "Bank Transfer",
          cash: "Cash Payment",
        },
      });

      if (!confirmed) {
        return;
      }

      switch (confirmed) {
        case "card":
          this.process();
          break;
        case "bank":
          await this.markAsPaid("Bank Transfer");
          break;
        case "cash":
          await this.markAsPaid("Cash");
          break;
      }
    },

    async markAsPaid(paymentType) {
      this.form.busy = true;

      const payload = paymentType === "Bank Transfer" ? { method: "BankTransfer" } : {};

      const response = await this.$http.post(`/invoices/${this.crud.data.id}/mark-paid`, payload);
      this.form.busy = false;

      if (!response || response.status !== 200) {
        window.toastr.error(
          `Something went wrong when trying to mark this invoice as Paid By ${paymentType}`,
          "Error"
        );
        return;
      }

      window.toastr.success(`Successfully marked the invoice as Paid by ${paymentType}`, "Success");
      await this.init();
    },

    unpay() {
      if (this.$can("revert", "invoices")) {
        window
          .sweetAlert({
            title: "Please confirm",
            text: "Please provide your password to UNPAY this invoice",
            content: {
              element: "input",
              attributes: {
                placeholder: "Type your password",
                type: "password",
              },
            },
            icon: "warning",
            dangerMode: true,
            buttons: {
              cancel: "Cancel",
              confirm: "Submit",
            },
          })
          .then((password) => {
            if (password) {
              this.$http
                .post("/auth/check-password", {
                  params: {
                    email: this.$auth.user() ? this.$auth.user().email : null,
                    password,
                  },
                })
                .then((response) => {
                  if (response.body && response.body.result) {
                    this.$http.put(`/invoices/${this.crud.data.id}/unpay`).then((res) => {
                      if (res.body && res.body.id) {
                        // noinspection JSUnresolvedFunction
                        this.$root.reload();
                        window.toastr.info("Successfully unpaid the invoice!", "Success");
                      } else {
                        window.toastr.error(
                          "Something went wrong when trying to mark the invoice as unpaid."
                        );
                      }
                    });
                  } else {
                    window.toastr.error("Incorrect password, doing nothing");
                  }
                });
            }
          });
      }
    },

    createInvoiceByQuote() {
      window
        .sweetAlert({
          title: "Are you sure?",
          text: "An invoice will be created based on current input, proceed?",
          icon: "warning",
          dangerMode: true,
          buttons: {
            cancel: "No",
            confirm: "Yes!",
          },
        })
        .then((confirmed) => {
          if (confirmed) {
            this.form.items = this.crud.data.items;
            this.form.is_quote = 0;
            this.form.accepted = 0;

            this.crud.submit(this.form, "create").then((response) => {
              window.toastr.success(
                `Invoice created based on ${this.crud.data.invoice_number}!`,
                "Success"
              );

              this.$router.push(
                {
                  name: "invoices.edit",
                  params: { id: response.id },
                },
                () => {
                  // noinspection JSUnresolvedFunction
                  this.$root.reload();
                }
              );
            });
          }
        });
    },

    send() {
      const type = this.crud.data.is_quote ? "quote" : "invoice";

      window
        .sweetAlert({
          title: "Are you sure?",
          text: `Are you sure that you want to send this ${type} to the customer?`,
          icon: "warning",
          dangerMode: true,
          buttons: {
            cancel: "No",
            email: "Email Customer",
            sms: "SMS Customer",
          },
        })
        .then((confirmed) => {
          if (confirmed) {
            this.sending = true;
            const channel = confirmed;

            this.$http
              .post(`/invoices/${this.crud.data.id}/${channel}-customer?medium=CCT`)
              .then((response) => {
                const type = this.crud.data.is_quote ? "quote" : "invoice";

                if (response.body.message !== undefined && response.body.message === "success") {
                  window.toastr.success(
                    `Successfully resent this ${type} to ${response.body.recipient.name} via ${channel}.`,
                    "Success"
                  );
                  this.init();
                }

                this.sending = false;
              });
          }
        });
    },

    refund() {
      if (this.crud.data.is_quote) {
        window.toastr.error("Cannot refund a quote!", "Error");
      } else if (this.crud.data.status !== "Paid") {
        window.toastr.error("The invoice is not paid!", "Error");
      } else if (this.crud.data.transaction_note === "Cash Payment") {
        window.toastr.error("Cannot refund an invoice paid by cash!", "Error");
      } else if (this.crud.data.transaction_note.length === 0) {
        window.toastr.error("Invalid invoice for refund!", "Error");
      } else {
        window
          .sweetAlert({
            title: "Are you sure?",
            text: "Are you sure that you want to refund this invoice?",
            icon: "warning",
            dangerMode: true,
            buttons: {
              cancel: "No",
              confirm: "Yes, refund this invoice!",
            },
          })
          .then((confirmed) => {
            if (confirmed) {
              this.form.busy = true;
              this.refunding = true;

              this.$http.post(`/invoices/${this.crud.data.id}/refund`).then((response) => {
                if (response.body.status === "successful") {
                  window.toastr.success("This invoice has been refunded.", "Refunded");
                } else {
                  window.toastr.error(
                    "An error occurred when trying to refund this invoice.",
                    "Failed"
                  );
                }

                this.form.busy = false;
                this.refunding = false;

                // Reload
                this.initialised = false;
                this.init();
              });
            }
          });
      }
    },

    addCustomTitle(title, index) {
      this.validBrandInvoiceItems.push(title);
      this.crud.data.items[index].title = title;

      this.form.errors.clear();
    },

    invoiceItemSelect(selectedTitle, id) {
      const invoiceItems = this.crud.data.job.brand.invoice_items;
      const index = invoiceItems.map((invoiceItem) => invoiceItem.title).indexOf(selectedTitle);

      if (invoiceItems[index] && this.$lodash.has(invoiceItems[index], "cost")) {
        const description = this.$lodash.get(invoiceItems[index], "description");
        if (description) {
          this.crud.data.items[id].description = description;
        }

        const cost = this.$lodash.get(invoiceItems[index], "cost");
        if (cost) {
          this.crud.data.items[id].cost = cost;
        }

        const serviceTypeId = this.$lodash.get(invoiceItems[index], "service_type_id");
        if (serviceTypeId) {
          this.crud.data.items[id].service_type_id = invoiceItems[index].service_type_id;
          this.crud.data.items[id].service_type_name = invoiceItems[index]?.service_type?.name;
        }
      }
    },

    royaltyTypeChanged(royaltyType) {
      if (royaltyType === "Standard") {
        this.form.royalty_value = null;
      }
    },

    voucherChanged(newCode) {
      this.selectedNewVoucher = this.getVoucherByCode(newCode);

      this.refreshActualVoucherLineItem();
    },

    getVoucherByCode(code) {
      if (!code) {
        return null;
      }

      return this.$lodash.find(this.allVouchers, (voucher) => voucher.code === code);
    },
  },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
h1 {
  margin-bottom: -20px;
}

.main-heading {
  min-height: 85px;
}

.total-div {
  width: 400px;
  background-color: #f2f2f2;
  padding: 20px;
  border-radius: 4px;
}

.voucher-code-wrapper {
  margin-right: -55px;

  ::v-deep & .invalid-feedback,
  ::v-deep & #voucher_code {
    text-align: right;
  }
}
</style>
